import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Editor, Toolbar } from 'ngx-editor';
import { DozentService, Exercise } from '../../../../services/dozent.service';

interface AnswerOption {
  text: string;
  is_correct: boolean;
}

interface ExerciseQuestion {
  id: string;
  exerciseType: string;
  problemStatement: string;
  answerType: string;
  answer: any;
  hint: string;
  additionalContent: string;
  points: number;
  expanded?: boolean;
}

@Component({
  selector: 'app-exercise-editor',
  templateUrl: './exercise-editor.component.html',
  styleUrl: './exercise-editor.component.scss',
})
export class ExerciseEditorComponent implements OnInit, OnDestroy {
  editor!: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic', 'underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    ['blockquote', 'code'],
    ['link'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right'],
  ];

  exerciseId!: string;
  exercise: Exercise | null = null;
  isLoading = true;
  isSaving = false;

  exerciseTypes = [
    { value: 'multiple-choice', label: 'Multiple Choice' },
    { value: 'open-ended', label: 'Open Ended' },
    { value: 'fill-in-blank', label: 'Fill in the Blank' },
    { value: 'true-false', label: 'True / False' },
    { value: 'exam', label: 'Exam' },
  ];

  difficultyLevels = [
    { value: 'easy', labelKey: 'pages.dozent.exerciseEditor.difficultyEasy' },
    { value: 'medium', labelKey: 'pages.dozent.exerciseEditor.difficultyMedium' },
    { value: 'hard', labelKey: 'pages.dozent.exerciseEditor.difficultyHard' },
  ];

  scopeOptions = [
    { value: 'small', labelKey: 'pages.dozent.exerciseEditor.scopeSmall' },
    { value: 'medium', labelKey: 'pages.dozent.exerciseEditor.scopeMedium' },
    { value: 'large', labelKey: 'pages.dozent.exerciseEditor.scopeLarge' },
  ];

  // Editable metadata fields
  title = '';
  description = '';
  exerciseType = '';
  difficultyLevel = 'medium';
  learningGoals = '';
  scope = 'medium';
  maxScore = 100;
  timeLimitMinutes: number | null = null;
  language = 'de';
  autoProcessSubmissions = true;
  chatPracticeEnabled = false;

  // Multi-question structure (the only format)
  questions: ExerciseQuestion[] = [];

  // Kept only so legacy template references compile while the legacy block
  // is removed from the HTML. Always false.
  isLegacyFormat = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dozentService: DozentService,
    private toastrService: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.editor = new Editor();
    this.exerciseId = this.route.snapshot.params['id'];
    this.loadExercise();
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  loadExercise() {
    this.isLoading = true;
    this.dozentService.getExercise(this.exerciseId).subscribe({
      next: (exercise: Exercise) => {
        this.exercise = exercise;
        this.populateForm(exercise);
        this.isLoading = false;
      },
      error: () => {
        this.toastrService.danger(this.t.instant('toasts.error'), '');
        this.isLoading = false;
      },
    });
  }

  populateForm(ex: Exercise) {
    this.title = ex.title;
    this.description = ex.description || '';
    this.exerciseType = ex.exercise_type;
    this.maxScore = ex.max_score;
    this.timeLimitMinutes = ex.time_limit_minutes ?? null;
    this.autoProcessSubmissions = ex.auto_process_submissions ?? true;
    this.chatPracticeEnabled = ex.chat_practice_enabled ?? false;
    this.difficultyLevel = ex.content?.difficulty_level || 'medium';
    this.learningGoals = ex.content?.learning_goals || '';
    this.scope = ex.content?.scope || 'medium';
    this.language = ex.content?.language || 'de';

    // Always use the multi-question format. Legacy single-question content is
    // transparently upgraded so the dozent only ever sees the new UI.
    this.isLegacyFormat = false;

    if (Array.isArray(ex.content?.questions) && ex.content.questions.length > 0) {
      this.questions = ex.content.questions.map((q: any, idx: number) => ({
        id: q.id || `q-${idx}`,
        exerciseType: q.exerciseType || ex.exercise_type,
        problemStatement: q.problemStatement || '',
        answerType: q.answerType || 'text',
        answer: q.answer ?? '',
        hint: q.hint || '',
        additionalContent: q.additionalContent || '',
        points: q.points ?? 10,
        expanded: idx === 0,
      }));
    } else {
      // Upgrade legacy single-question content in place.
      const legacyAnswer = ex.content?.answer;
      let answer: any = '';
      let answerType: string = 'text';

      if (this.isChoiceType(this.exerciseType)) {
        answerType = 'multiple_choice';
        if (Array.isArray(legacyAnswer)) {
          answer = legacyAnswer.map((a: any) => ({ text: a.text || '', is_correct: !!a.is_correct }));
        } else if (this.exerciseType === 'true-false') {
          answer = [
            { text: 'Wahr', is_correct: true },
            { text: 'Falsch', is_correct: false },
          ];
        } else {
          answer = [];
        }
      } else if (this.exerciseType === 'fill-in-blank') {
        answerType = 'fill_in_the_blank';
        answer = {
          sentence: legacyAnswer?.sentence || '',
          correct_answers: Array.isArray(legacyAnswer?.correct_answers) ? [...legacyAnswer.correct_answers] : [''],
        };
      } else {
        answerType = 'text';
        answer = typeof legacyAnswer === 'string' ? legacyAnswer : '';
      }

      this.questions = [{
        id: `q-${Date.now()}`,
        exerciseType: this.exerciseType,
        problemStatement: ex.content?.problem_statement || '',
        answerType,
        answer,
        hint: ex.content?.hint || '',
        additionalContent: ex.content?.additional_educational_content || '',
        points: this.maxScore || 100,
        expanded: true,
      }];
    }

    // If a new/empty exercise has no questions, start with one.
    if (this.questions.length === 0) {
      this.addQuestion();
    }
  }

  // ── Question helpers ──

  isChoiceType(type: string): boolean {
    return ['multiple-choice', 'true-false', 'multiple-answer'].includes(type);
  }

  toggleQuestion(index: number) {
    this.questions[index].expanded = !this.questions[index].expanded;
  }

  addQuestion() {
    const type = this.exerciseType || 'open-ended';
    let defaultAnswer: any = '';
    if (type === 'multiple-choice') {
      defaultAnswer = [
        { text: '', is_correct: true },
        { text: '', is_correct: false },
        { text: '', is_correct: false },
        { text: '', is_correct: false },
      ];
    } else if (type === 'true-false') {
      defaultAnswer = [
        { text: 'Wahr', is_correct: true },
        { text: 'Falsch', is_correct: false },
      ];
    } else if (type === 'fill-in-blank') {
      defaultAnswer = { sentence: '', correct_answers: [''] };
    }

    this.questions.push({
      id: `q-${Date.now()}`,
      exerciseType: type,
      problemStatement: '',
      answerType: type === 'multiple-choice' ? 'multiple_choice' :
                  type === 'true-false' ? 'multiple_choice' :
                  type === 'fill-in-blank' ? 'fill_in_the_blank' : 'text',
      answer: defaultAnswer,
      hint: '',
      additionalContent: '',
      points: 10,
      expanded: true,
    });
  }

  removeQuestion(index: number) {
    this.questions.splice(index, 1);
  }

  moveQuestion(fromIdx: number, direction: 'up' | 'down') {
    const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
    if (toIdx < 0 || toIdx >= this.questions.length) return;
    const temp = this.questions[fromIdx];
    this.questions[fromIdx] = this.questions[toIdx];
    this.questions[toIdx] = temp;
  }

  getQuestionAnswerOptions(question: ExerciseQuestion): AnswerOption[] {
    return Array.isArray(question.answer) ? question.answer : [];
  }

  addQuestionOption(question: ExerciseQuestion) {
    if (!Array.isArray(question.answer)) {
      question.answer = [];
    }
    question.answer.push({ text: '', is_correct: false });
  }

  removeQuestionOption(question: ExerciseQuestion, optIndex: number) {
    if (Array.isArray(question.answer)) {
      question.answer.splice(optIndex, 1);
    }
  }

  getQuestionFillAnswers(question: ExerciseQuestion): string[] {
    return question.answer?.correct_answers || [''];
  }

  addQuestionFillAnswer(question: ExerciseQuestion) {
    if (!question.answer || typeof question.answer !== 'object') {
      question.answer = { sentence: '', correct_answers: [] };
    }
    question.answer.correct_answers.push('');
  }

  removeQuestionFillAnswer(question: ExerciseQuestion, idx: number) {
    question.answer?.correct_answers?.splice(idx, 1);
  }

  updateFillAnswer(question: ExerciseQuestion, idx: number, value: string) {
    if (!question.answer?.correct_answers) return;
    question.answer.correct_answers[idx] = value;
  }

  getExerciseTypeLabel(type: string): string {
    const found = this.exerciseTypes.find((t) => t.value === type);
    return found ? found.label : type;
  }

  trackByIndex(index: number): number { return index; }

  /** Release flag saves immediately (like the publish/shield actions in the
   *  list) — it's an action, not a form field. Waiting for the Speichern
   *  button silently dropped the flag when the dozent navigated away. */
  onToggleChatPractice(next: boolean) {
    this.chatPracticeEnabled = next;
    this.dozentService
      .updateExercise(this.exerciseId, { chat_practice_enabled: next })
      .subscribe({
        error: () => {
          this.chatPracticeEnabled = !next;
          this.toastrService.danger(this.t.instant('toasts.error'), '');
        },
      });
  }

  // ── Save ──

  buildContent(): any {
    return {
      difficulty_level: this.difficultyLevel,
      learning_goals: this.learningGoals,
      scope: this.scope,
      language: this.language,
      questions: this.questions.map((q) => ({
        exerciseType: q.exerciseType,
        problemStatement: q.problemStatement,
        answerType: q.answerType,
        answer: q.answer,
        hint: q.hint,
        additionalContent: q.additionalContent,
        points: q.points,
      })),
    };
  }

  onSave() {
    if (!this.title) return;
    this.isSaving = true;
    const payload: Partial<Exercise> = {
      title: this.title,
      description: this.description || undefined,
      exercise_type: this.exerciseType,
      max_score: this.maxScore,
      time_limit_minutes: this.timeLimitMinutes ?? undefined,
      auto_process_submissions: this.autoProcessSubmissions,
      chat_practice_enabled: this.chatPracticeEnabled,
      content: this.buildContent(),
    };

    this.dozentService.updateExercise(this.exerciseId, payload).subscribe({
      next: () => {
        this.toastrService.success(
          this.t.instant('pages.dozent.exerciseEditor.saved'),
          this.title
        );
        this.isSaving = false;
      },
      error: () => {
        this.toastrService.danger(this.t.instant('toasts.error'), '');
        this.isSaving = false;
      },
    });
  }

  /** Exercises from the Creator library are edited in the Creator (Q95/P4). */
  onEditInCreator() {
    if (this.exercise?.creator_item_id) {
      this.router.navigate(['/creator/workspace'], { queryParams: { item: this.exercise.creator_item_id } });
    }
  }

  onBack() {
    this.router.navigate(['/dozent/exercises']);
  }
}
